import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, ArrowUpRight } from "lucide-react";
import { getService, services } from "../data/services";
import { Reveal, Overline } from "../components/shared";
import ContactForm from "../components/ContactForm";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getService(slug);

  if (!service) {
    return (
      <div className="mx-auto flex min-h-screen max-w-3xl flex-col items-center justify-center px-6 text-center">
        <h1 className="font-display text-4xl text-white">Service not found</h1>
        <Link
          to="/"
          className="mt-6 rounded-full bg-gradient-to-r from-[#00f0ff] to-[#b026ff] px-6 py-3 font-semibold text-[#05050a]"
        >
          Back to home
        </Link>
      </div>
    );
  }

  const Icon = service.icon;
  const others = services.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <div data-testid="service-detail-page" className="pt-28">
      {/* Header */}
      <section className="relative overflow-hidden">
        <div className="glow-blob left-[-6%] top-0 h-80 w-80 bg-[#00f0ff]" />
        <div className="glow-blob right-[-6%] top-10 h-96 w-96 bg-[#b026ff]" />
        <div className="relative mx-auto max-w-7xl px-6 py-12 md:py-16">
          <Link
            to="/"
            data-testid="service-back-link"
            className="inline-flex items-center gap-2 font-mono-tech text-xs uppercase tracking-widest text-[#a0aec0] transition-colors hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>

          <div className="mt-10 grid items-center gap-12 lg:grid-cols-2">
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-[#00f0ff]"
              >
                <Icon className="h-8 w-8" />
              </motion.div>
              <Overline>Gabicom Service</Overline>
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1 }}
                className="font-display mt-4 text-4xl font-bold leading-tight text-white md:text-6xl"
              >
                {service.title}
              </motion.h1>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.7, delay: 0.3 }}
                className="mt-6 max-w-lg text-base leading-relaxed text-[#a0aec0] md:text-lg"
              >
                {service.intro}
              </motion.p>
              <motion.a
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.45 }}
                href="#service-contact"
                data-testid="service-cta"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#00f0ff] to-[#b026ff] px-7 py-3.5 font-semibold text-[#05050a] transition-transform hover:scale-105"
              >
                Request this service <ArrowUpRight className="h-5 w-5" />
              </motion.a>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative overflow-hidden rounded-3xl border border-white/10"
            >
              <img
                src={service.img}
                alt={service.title}
                data-testid="service-hero-image"
                className="animate-kenburns h-[380px] w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#05050a] via-transparent to-transparent" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-7xl px-6 py-12 md:py-16">
        <Reveal>
          <h2 className="font-display text-3xl text-white md:text-4xl">What&apos;s included</h2>
        </Reveal>
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {service.features.map((f, i) => (
            <Reveal key={f} delay={i * 0.07}>
              <div className="flex items-start gap-4 rounded-2xl border border-white/10 bg-[#0d0e15] p-6">
                <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-[#00f0ff] to-[#b026ff] text-[#05050a]">
                  <Check className="h-4 w-4" />
                </span>
                <p className="text-[#e2e8f0]">{f}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Contact */}
      <section
        id="service-contact"
        data-testid="service-contact-section"
        className="mx-auto max-w-7xl px-6 py-12 md:py-16"
      >
        <div className="grid gap-12 lg:grid-cols-2">
          <div>
            <Overline>Get started</Overline>
            <Reveal delay={0.1}>
              <h2 className="font-display mt-5 text-4xl font-semibold leading-tight text-white md:text-5xl">
                Request {service.title}
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-md text-[#a0aec0]">
                Fill out the form and our team will get back to you quickly to discuss your
                needs. Your enquiry is pre-tagged with this service.
              </p>
            </Reveal>
            <div className="mt-8 space-y-2 text-sm text-[#a0aec0]">
              <p>Email: sales@gabicomitsolutions.com</p>
              <p>Phone: 1.202.309.2266</p>
            </div>
          </div>
          <Reveal delay={0.2}>
            <ContactForm defaultService={service.title} />
          </Reveal>
        </div>
      </section>

      {/* Other services */}
      <section className="mx-auto max-w-7xl px-6 py-12 md:py-16">
        <Overline>Explore more</Overline>
        <h2 className="font-display mt-4 text-3xl text-white md:text-4xl">Other services</h2>
        <div className="mt-8 grid gap-6 sm:grid-cols-3">
          {others.map((s) => {
            const OIcon = s.icon;
            return (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`other-service-${s.slug}`}
                className="group rounded-3xl border border-white/10 bg-[#0d0e15] p-6 transition-colors hover:border-[#00f0ff]/50"
              >
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-[#00f0ff] transition-colors group-hover:text-[#b026ff]">
                  <OIcon className="h-6 w-6" />
                </div>
                <h3 className="font-display text-lg text-white">{s.title}</h3>
                <p className="mt-2 text-sm text-[#a0aec0]">{s.short}</p>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}
